import { ApplicationConfig, importProvidersFrom, inject, provideAppInitializer } from '@angular/core';
import { HttpBackend, provideHttpClient, withInterceptors } from '@angular/common/http';
import { provideRouter } from '@angular/router';
import { ServiceWorkerModule } from '@angular/service-worker';
import { provideAnimationsAsync } from '@angular/platform-browser/animations/async';
import { Observable } from 'rxjs';
import { TranslateLoader, TranslateModule } from '@ngx-translate/core';
import { OAuthModule } from 'angular-oauth2-oidc';
import { MultiTranslateHttpLoader } from 'ngx-translate-multi-http-loader';
import { ApiModule } from '@nx-customer-apps/api-planning-projects';
import { ViHeaderDomainModule } from '@vi-succ/vi-header-domain';
import { environment } from '../environments/environment';
import { AuthService } from './core/services';
import { accessTokenInterceptor, apiKeyInterceptor } from './core/interceptors';
import { appRoutes } from './app.routes';
import { ProductsModule } from '@nx-customer-apps/components';
import { MAT_RIPPLE_GLOBAL_OPTIONS } from '@angular/material/core';

function HttpLoaderFactory(http: HttpBackend) {
    return new MultiTranslateHttpLoader(http, [
        {
            prefix: `${environment.translationsBaseUrl}/phraseapp-proxy/projects/${environment.phraseappProjectId.questionFlow}/locales/`,
            suffix: '/download?file_format=simple_json'
        },
        {
            prefix: `${environment.translationsBaseUrl}/phraseapp-proxy/projects/${environment.phraseappProjectId.designSystemWeb}/locales/`,
            suffix: '/download?file_format=simple_json'
        },
        {
            prefix: `${environment.translationsBaseUrl}/phraseapp-proxy/projects/${environment.phraseappProjectId.planningToolsConfig}/locales/`,
            suffix: '/download?file_format=simple_json'
        },
        {
            prefix: `${environment.translationsBaseUrl}/phraseapp-proxy/projects/${environment.phraseappProjectId.components}/locales/`,
            suffix: '/download?file_format=simple_json'
        }
    ]);
}

export function authAppInitializerFactory(authService: AuthService): () => Observable<void> {
    return () => authService.runInitialLoginSequence();
}

export const appConfig: ApplicationConfig = {
    providers: [
        provideRouter(appRoutes),
        importProvidersFrom(OAuthModule.forRoot()),
        provideHttpClient(withInterceptors([accessTokenInterceptor, apiKeyInterceptor])),
        provideAnimationsAsync(),
        importProvidersFrom(
            ApiModule.forRoot({ rootUrl: environment.baseUrl }),
            ViHeaderDomainModule.forRoot({
                baseUrl: environment.baseUrl
            }),
            ProductsModule.forRoot(environment.apiPlanningProjectsConfig)
        ),
        importProvidersFrom(
            TranslateModule.forRoot({
                loader: {
                    provide: TranslateLoader,
                    useFactory: HttpLoaderFactory,
                    deps: [HttpBackend]
                }
            }),
            ServiceWorkerModule.register('ngsw-worker.js', {
                enabled: environment.serviceWorker,
                registrationStrategy: 'registerWhenStable:30000'
            })
        ),
        provideAppInitializer(() => {
            const authService = inject(AuthService);
            const initializerFn = authAppInitializerFactory(authService);
            return initializerFn();
        }),
        {
            provide: MAT_RIPPLE_GLOBAL_OPTIONS,
            useValue: { disabled: true }
        }
    ]
};
